import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import AddressBlock from './AddressBlock';
import profileBlackIcon from '../assets/\u041f\u0440\u043e\u0444\u0438\u043b\u044c-\u0447\u0451\u0440\u043d\u043e\u0435 (1).png';
import boxIcon from '../assets/\u043a\u043e\u0440\u043e\u0431\u043a\u0430.png';
import { apiClient } from '../api/client';
import type { User, UserOrder } from '../api/types';

interface ProfileProps {
  userId: string | null;
  onLogoClick?: () => void;
  onCatalogToggle?: () => void;
  isCatalogOpen?: boolean;
  onFavoritesClick?: () => void;
  onCartClick?: () => void;
  onLogout?: () => void;
  onLoginClick?: () => void;
  onRegisterClick?: () => void;
}

export default function Profile({
  userId,
  onLogoClick: _onLogoClick,
  onCatalogToggle: _onCatalogToggle,
  isCatalogOpen: _isCatalogOpen = false,
  onFavoritesClick: _onFavoritesClick,
  onCartClick: _onCartClick,
  onLogout,
  onLoginClick,
  onRegisterClick,
}: ProfileProps) {
  const addressRef = useRef<HTMLDivElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [user, setUser] = useState<User | null>(null);
  const [orders, setOrders] = useState<UserOrder[]>([]);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    avatar_url: '',
  });
  const isAuthenticated = Boolean(user && !error);

  useEffect(() => {
    const loadProfile = async () => {
      if (!userId) {
        setUser(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const userResponse = await apiClient.getUser(userId);
        if (!userResponse.success || !userResponse.data) {
          throw new Error(userResponse.error || 'Failed to load profile');
        }

        setUser(userResponse.data);
        setEditForm({
          first_name: userResponse.data.first_name || '',
          last_name: userResponse.data.last_name || '',
          phone: formatPhone(userResponse.data.phone || ''),
          avatar_url: userResponse.data.avatar_url || '',
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        console.error('Error loading profile:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [userId]);

  useEffect(() => {
    const loadOrders = async () => {
      if (!userId) {
        setOrders([]);
        setIsOrdersLoading(false);
        return;
      }

      setIsOrdersLoading(true);
      try {
        const ordersResponse = await apiClient.getUserOrders(userId);
        if (!ordersResponse.success || !ordersResponse.data) {
          setOrders([]);
          return;
        }

        setOrders(ordersResponse.data);
      } catch (err) {
        console.error('Error loading orders:', err);
        setOrders([]);
      } finally {
        setIsOrdersLoading(false);
      }
    };

    loadOrders();
  }, [userId]);

  const handleEdit = () => {
    if (user) {
      setEditForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: formatPhone(user.phone || ''),
        avatar_url: user.avatar_url || '',
      });
    }
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (user) {
      setEditForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: formatPhone(user.phone || ''),
        avatar_url: user.avatar_url || '',
      });
    }
    setIsEditing(false);
  };

  const handleAvatarPickClick = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('\u0412\u044b\u0431\u0435\u0440\u0438\u0442\u0435 \u0438\u0437\u043e\u0431\u0440\u0430\u0436\u0435\u043d\u0438\u0435');
      event.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('\u0420\u0430\u0437\u043c\u0435\u0440 \u0438\u0437\u043e\u0431\u0440\u0430\u0436\u0435\u043d\u0438\u044f \u0434\u043e\u043b\u0436\u0435\u043d \u0431\u044b\u0442\u044c \u043d\u0435 \u0431\u043e\u043b\u044c\u0448\u0435 5 \u041c\u0411');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setEditForm((prev) => ({ ...prev, avatar_url: reader.result as string }));
      }
    };
    reader.onerror = () => {
      alert('\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u043f\u0440\u043e\u0447\u0438\u0442\u0430\u0442\u044c \u0438\u0437\u043e\u0431\u0440\u0430\u0436\u0435\u043d\u0438\u0435');
    };
    reader.readAsDataURL(file);
  };

  const handleSaveEdit = async () => {
    if (!user || !userId) return;

    try {
      const updateData: { first_name?: string; last_name?: string; phone?: string; avatar_url?: string } = {};

      if (editForm.first_name !== (user.first_name || '')) {
        updateData.first_name = editForm.first_name || undefined;
      }
      if (editForm.last_name !== (user.last_name || '')) {
        updateData.last_name = editForm.last_name || undefined;
      }
      const normalizedPhone = normalizePhone(editForm.phone);
      if (editForm.phone.trim() && !normalizedPhone) {
        alert('\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043a\u043e\u0440\u0440\u0435\u043a\u0442\u043d\u044b\u0439 \u0442\u0435\u043b\u0435\u0444\u043e\u043d');
        return;
      }
      if ((normalizedPhone || '') !== (normalizePhone(user.phone) || '')) {
        updateData.phone = normalizedPhone || undefined;
      }
      if (editForm.avatar_url !== (user.avatar_url || '')) {
        updateData.avatar_url = editForm.avatar_url || undefined;
      }

      if (Object.keys(updateData).length === 0) {
        setIsEditing(false);
        return;
      }

      const response = await apiClient.updateUser(userId, updateData);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update profile');
      }

      setUser(response.data);
      setIsEditing(false);
      alert('\u041f\u0440\u043e\u0444\u0438\u043b\u044c \u0443\u0441\u043f\u0435\u0448\u043d\u043e \u043e\u0431\u043d\u043e\u0432\u043b\u0435\u043d');
    } catch (err) {
      console.error('Error updating profile:', err);
      alert(err instanceof Error ? err.message : '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u043e\u0431\u043d\u043e\u0432\u0438\u0442\u044c \u043f\u0440\u043e\u0444\u0438\u043b\u044c');
    }
  };

  const normalizePhone = (phone?: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length === 10) {
      return `7${cleaned}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('8')) {
      return `7${cleaned.slice(1)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('7')) {
      return cleaned;
    }

    return '';
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return '';
    const normalized = normalizePhone(phone);
    if (!normalized) return phone;

    return `+${normalized[0]} ${normalized.slice(1, 4)} ${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
  };

  const formatPhoneInput = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    const normalized = digits.startsWith('8') ? `7${digits.slice(1)}` : digits.startsWith('7') ? digits : `7${digits}`;
    const limited = normalized.slice(0, 11);

    let result = '+7';
    if (limited.length > 1) {
      result += ` ${limited.slice(1, 4)}`;
    }
    if (limited.length > 4) {
      result += ` ${limited.slice(4, 7)}`;
    }
    if (limited.length > 7) {
      result += `-${limited.slice(7, 9)}`;
    }
    if (limited.length > 9) {
      result += `-${limited.slice(9, 11)}`;
    }
    return result;
  };

  const truncateName = (value?: string, max: number = 10) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (trimmed.length <= max) return trimmed;
    return `${trimmed.slice(0, max)}...`;
  };

  const getDisplayName = () => {
    if (!user) return '';
    const firstName = truncateName(user.first_name, 10);
    const lastName = truncateName(user.last_name, 10);
    if (firstName || lastName) {
      return [firstName, lastName].filter(Boolean).join(' ');
    }
    if (user.display_name) return truncateName(user.display_name, 20);
    if (user.phone) return formatPhone(user.phone);
    if (user.email) return user.email.split('@')[0];
    return '\u041f\u043e\u043b\u044c\u0437\u043e\u0432\u0430\u0442\u0435\u043b\u044c';
  };

  const orderImage = 'https://www.figma.com/api/mcp/asset/404868db-e8a5-4a93-9ecb-c00ae8e70e84';
  const avatarImage = 'https://www.figma.com/api/mcp/asset/183a2543-855a-4fe5-861c-2e5bc0cbc4a4';
  const emptyStateClass =
    'flex min-h-[280px] w-full items-center justify-center rounded-[24px] border border-[#e6ebfb] bg-white/70 px-6 text-center shadow-[0_6px_20px_rgba(0,0,0,0.04)]';

  return (
    <div className="relative flex-1 bg-[#f3f3f3]">
      <main className="relative mx-auto w-full max-w-[1520px] px-4 pt-[20px] pb-[32px] sm:px-6 lg:px-8">
        <div className="mb-[20px]">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex flex-wrap items-center gap-x-[32px] gap-y-4 pl-1 sm:pl-3 lg:pl-5">
              <div className="flex items-center gap-4">
              <img src={profileBlackIcon} alt="\u041f\u0440\u043e\u0444\u0438\u043b\u044c" className="h-[38px] w-[38px] flex-shrink-0 sm:h-[46px] sm:w-[46px] lg:h-[50px] lg:w-[50px]" />
              <h1 className="text-[30px] font-extrabold leading-none tracking-[1px] text-black sm:text-[36px] lg:text-[40px] lg:tracking-[2px]">
                {'\u041f\u0440\u043e\u0444\u0438\u043b\u044c'}
              </h1>
              </div>

              {isAuthenticated && (
              <div className="flex items-center gap-4">
                <img src={boxIcon} alt="\u0417\u0430\u043a\u0430\u0437\u044b" className="h-[38px] w-[38px] flex-shrink-0 sm:h-[46px] sm:w-[46px] lg:h-[50px] lg:w-[50px]" />
                <h1 className="text-[30px] font-extrabold leading-none tracking-[1px] text-black sm:text-[36px] lg:text-[40px] lg:tracking-[2px]">
                  {'\u0417\u0430\u043a\u0430\u0437\u044b:'}
                </h1>
              </div>
            )}
            </div>

            {isAuthenticated && (
              <div ref={addressRef} className="w-full max-w-[360px]" style={{ height: '80px' }}>
                <AddressBlock userId={userId} className="h-full" />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-[20px] xl:flex-row xl:items-start">
            <div className="flex flex-col gap-[20px] xl:w-[470px] xl:flex-shrink-0">
              {(isLoading || error || user) && (
                <div className={`relative w-full rounded-[24px] bg-white p-[16px] shadow-[0_6px_20px_rgba(0,0,0,0.05)] sm:p-[18px] ${isEditing ? 'min-h-[248px]' : 'min-h-[146px]'}`}>
                  {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-[20px] text-gray-600">{'\u0417\u0430\u0433\u0440\u0443\u0437\u043a\u0430 \u043f\u0440\u043e\u0444\u0438\u043b\u044f...'}</p>
                    </div>
                  ) : error ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-[20px] text-red-600">{'\u041e\u0448\u0438\u0431\u043a\u0430:'} {error}</p>
                    </div>
                  ) : user ? (
                    <div className="flex h-full flex-col gap-[16px] sm:flex-row sm:items-start sm:gap-[20px]">
                      {isEditing ? (
                        <button
                          type="button"
                          onClick={handleAvatarPickClick}
                          className="group relative h-[96px] w-[96px] overflow-hidden rounded-full ring-2 ring-[#d9e1ff] transition-all duration-200 hover:ring-[#5d76cb] sm:h-[110px] sm:w-[110px] flex-shrink-0"
                        >
                          <img src={editForm.avatar_url || user.avatar_url || avatarImage} alt="\u0410\u0432\u0430\u0442\u0430\u0440" className="w-full h-full object-cover" />
                          <span className="absolute inset-0 bg-black/45 text-white text-[12px] font-semibold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            {'\u0418\u0437\u043c\u0435\u043d\u0438\u0442\u044c \u0444\u043e\u0442\u043e'}
                          </span>
                        </button>
                      ) : (
                        <img src={user.avatar_url || avatarImage} alt="\u0410\u0432\u0430\u0442\u0430\u0440" className="h-[88px] w-[88px] rounded-full object-cover sm:h-[100px] sm:w-[100px] flex-shrink-0" />
                      )}

                      <div className={`flex flex-col h-full flex-1 ${isEditing ? 'justify-start pt-[4px]' : 'justify-center'}`}>
                        {isEditing ? (
                          <>
                            <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarFileChange} />
                            <input
                              type="text"
                              value={editForm.first_name}
                              onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                              placeholder={'\u0418\u043c\u044f'}
                              className="mb-[8px] h-[42px] w-full rounded-[12px] border border-[#d6d6d6] bg-[#fafbff] px-[12px] text-[18px] font-semibold leading-[26px] text-black outline-none transition-colors focus:border-[#5d76cb] focus:bg-white sm:text-[20px]"
                            />
                            <input
                              type="text"
                              value={editForm.last_name}
                              onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                              placeholder={'\u0424\u0430\u043c\u0438\u043b\u0438\u044f'}
                              className="mb-[8px] h-[42px] w-full rounded-[12px] border border-[#d6d6d6] bg-[#fafbff] px-[12px] text-[18px] font-semibold leading-[26px] text-black outline-none transition-colors focus:border-[#5d76cb] focus:bg-white sm:text-[20px]"
                            />
                            <input
                              type="tel"
                              value={editForm.phone}
                              onChange={(e) => setEditForm({ ...editForm, phone: formatPhoneInput(e.target.value) })}
                              placeholder={'\u0422\u0435\u043b\u0435\u0444\u043e\u043d'}
                              className="mb-[12px] h-[42px] w-full rounded-[12px] border border-[#d6d6d6] bg-[#fafbff] px-[12px] text-[16px] font-medium leading-[24px] text-black outline-none transition-colors focus:border-[#5d76cb] focus:bg-white sm:text-[18px]"
                            />
                            <div className="flex gap-[10px]">
                              <button type="button" onClick={handleSaveEdit} className="h-[38px] px-[18px] rounded-[12px] bg-[#5d76cb] text-white text-[15px] font-semibold hover:bg-[#4d66bb] transition-colors">
                                {'\u0421\u043e\u0445\u0440\u0430\u043d\u0438\u0442\u044c'}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="h-[38px] px-[18px] rounded-[12px] border border-[#d0d0d0] bg-white text-[#4b4b4b] text-[15px] font-semibold hover:bg-[#f5f5f5] transition-colors"
                              >
                                {'\u041e\u0442\u043c\u0435\u043d\u0430'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <h2 className="mb-[10px] text-[24px] font-extrabold leading-[1.05] tracking-[1px] text-black sm:text-[30px] sm:tracking-[1.5px]">{getDisplayName()}</h2>
                            <p className="mb-[10px] text-[17px] font-normal leading-[26px] text-black sm:text-[20px] sm:tracking-[1px] sm:leading-[30px]">{formatPhone(user.phone)}</p>
                            <button onClick={handleEdit} className="text-[14px] font-normal text-[#4b93ff] leading-[20px] text-left hover:underline">
                              {'\u0420\u0435\u0434\u0430\u043a\u0442\u0438\u0440\u043e\u0432\u0430\u0442\u044c'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {user && (
                <button onClick={onLogout} className="flex h-[48px] w-[120px] items-center justify-center rounded-[18px] bg-white transition-all duration-300 hover:shadow-lg">
                  <span className="text-[18px] font-normal text-black tracking-[0.5px] sm:text-[20px]">{'\u0412\u044b\u0445\u043e\u0434'}</span>
                </button>
              )}

              {(!user || error) && (
                <div className="flex w-full max-w-[300px] flex-col gap-[12px] rounded-[24px] bg-white p-[18px] shadow-[0_6px_20px_rgba(0,0,0,0.05)]">
                  <p className="text-[16px] leading-[22px] text-[#4c5675]">
                    {'\u0412\u043e\u0439\u0434\u0438\u0442\u0435 \u0438\u043b\u0438 \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u0443\u0439\u0442\u0435\u0441\u044c, \u0447\u0442\u043e\u0431\u044b \u0432\u0438\u0434\u0435\u0442\u044c \u0437\u0430\u043a\u0430\u0437\u044b \u0438 \u0443\u043f\u0440\u0430\u0432\u043b\u044f\u0442\u044c \u043f\u0440\u043e\u0444\u0438\u043b\u0435\u043c.'}
                  </p>
                  {onLoginClick && (
                    <button onClick={onLoginClick} className="bg-[#5d76cb] text-white rounded-[16px] w-full h-[50px] flex items-center justify-center hover:bg-[#4d66bb] transition-all duration-300">
                      <span className="text-[20px] font-semibold text-white tracking-[1px]">{'\u0412\u043e\u0439\u0442\u0438'}</span>
                    </button>
                  )}
                  {onRegisterClick && (
                    <button
                      onClick={onRegisterClick}
                      className="bg-white rounded-[16px] w-full h-[50px] flex items-center justify-center hover:bg-[#f5f8ff] transition-all duration-300 border-2 border-[#5d76cb]"
                    >
                      <span className="text-[20px] font-semibold text-[#5d76cb] tracking-[1px]">{'\u0420\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044f'}</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {isAuthenticated && (
              <div className="flex flex-1 flex-col gap-[20px]">
                {isOrdersLoading ? (
                  <div className={emptyStateClass}>
                    <p className="text-[22px] font-medium text-[#5f6c8d]">{'\u0417\u0430\u0433\u0440\u0443\u0437\u043a\u0430 \u0437\u0430\u043a\u0430\u0437\u043e\u0432...'}</p>
                  </div>
                ) : orders.length === 0 ? (
                  <div className={emptyStateClass}>
                    <p className="text-[22px] font-medium text-[#5f6c8d]">{'\u041d\u0435\u0442 \u0437\u0430\u043a\u0430\u0437\u043e\u0432'}</p>
                  </div>
                ) : (
                  orders.map((order, index) => (
                    <div
                      key={order.id}
                      className="relative flex min-h-[200px] flex-col gap-[18px] rounded-[20px] bg-white p-[16px] lg:flex-row lg:items-start"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="h-[140px] w-full overflow-hidden rounded-[20px] bg-white p-[6px] sm:h-[150px] lg:mt-[25px] lg:w-[200px] lg:flex-shrink-0">
                        <img
                          src={order.image_url || orderImage}
                          alt={order.title}
                          className="w-full h-full object-contain scale-[1.05]"
                        />
                      </div>

                      <div className="flex flex-col justify-center h-full flex-1">
                        <h3 className="mb-[8px] text-[18px] font-normal leading-[26px] text-black sm:text-[22px] sm:leading-[30px]">{order.title}</h3>
                        <h4 className="mb-[8px] text-[24px] font-bold leading-[1.2] text-black sm:text-[30px] sm:leading-[60px]">{order.status}</h4>
                        {order.expected_date && (
                          <p className="mb-[8px] text-[17px] font-normal leading-[24px] text-[#747474] sm:text-[22px] sm:leading-[30px]">
                            {'\u041e\u0436\u0438\u0434\u0430\u0435\u043c\u0430\u044f \u0434\u0430\u0442\u0430 \u0434\u043e\u0441\u0442\u0430\u0432\u043a\u0438:'} {order.expected_date}
                          </p>
                        )}
                        {order.pickup_point && <p className="text-[17px] font-normal leading-[24px] text-black sm:text-[22px] sm:leading-[30px]">{order.pickup_point}</p>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
        </div>
      </main>
    </div>
  );
}
